import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BankReconciliationInvoiceAllocation,
  BankReconciliationCustomerIdentity,
  BankReconciliationInvoiceOptions,
  BankReconciliationSuggestion,
  BankReconciliationSuggestionEditRequest,
} from '../../api/client';
import { getBankReconciliationCustomers, getBankReconciliationInvoiceOptions } from '../../api/client';
import { fuzzySearchList } from '../../lib/fuzzySearch';
import DocumentLink from './DocumentLink';

interface Props {
  runId: string;
  suggestion: BankReconciliationSuggestion;
  saving: boolean;
  onClose: () => void;
  onSave: (request: BankReconciliationSuggestionEditRequest) => void;
  onOpenCustomer: (customerId: string, customerName: string) => void;
}

export default function EditSuggestionModal({
  runId, suggestion, saving, onClose, onSave, onOpenCustomer,
}: Props) {
  const { t } = useTranslation();
  const initialCustomer = suggestion.customer_id ?? suggestion.customer_candidates[0]?.customer_id ?? '';
  const [customerId, setCustomerId] = useState(initialCustomer);
  const [customerSearch, setCustomerSearch] = useState(
    suggestion.customer_name
      ?? suggestion.customer_candidates.find((item) => item.customer_id === initialCustomer)?.customer_name
      ?? '',
  );
  const [customerListOpen, setCustomerListOpen] = useState(false);
  const [activeCustomerIndex, setActiveCustomerIndex] = useState(0);
  const [confirmCustomer, setConfirmCustomer] = useState(false);
  const [payment, setPayment] = useState({ ...suggestion.payment });
  const [amounts, setAmounts] = useState<Record<string, string>>(() => Object.fromEntries(
    suggestion.allocations.map((item) => [`${item.document_type}:${item.document_number}`, String(item.amount)]),
  ));
  const [reason, setReason] = useState('');
  const [customers, setCustomers] = useState<BankReconciliationCustomerIdentity[]>([]);
  const [options, setOptions] = useState<BankReconciliationInvoiceOptions | null>(null);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [customerError, setCustomerError] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const [reload, setReload] = useState(0);
  const suggestOnLoad = useRef(initialCustomer !== suggestion.customer_id || suggestion.allocations.length === 0);
  const invoices = options?.invoices ?? [];
  const filteredCustomers = useMemo(
    () => fuzzySearchList(customers, customerSearch, (customer) => [
      customer.customer_name,
      customer.customer_id,
    ]),
    [customerSearch, customers],
  );
  const totalCents = Object.values(amounts).reduce((sum, amount) => sum + Math.round(Number(amount || 0) * 100), 0);
  const paymentCents = Math.round(Number(suggestion.transaction.amount) * 100);
  const invalidAmounts = invoices.some((invoice) => {
    const amount = Number(amounts[`${invoice.document_type}:${invoice.document_number}`] || 0);
    return !Number.isFinite(amount) || amount < 0 || amount > Number(invoice.open_balance)
      || Math.abs(amount * 100 - Math.round(amount * 100)) > 0.000001;
  });
  const staleAllocations = !!options && Object.entries(amounts).some(([key, value]) => (
    Number(value) > 0 && !invoices.some((invoice) => `${invoice.document_type}:${invoice.document_number}` === key)
  ));
  const invalidTotal = !Number.isFinite(totalCents) || totalCents > paymentCents || invalidAmounts || staleAllocations;
  const useSuggested = (next: BankReconciliationInvoiceOptions) => setAmounts(Object.fromEntries(
    next.suggested_allocations.map((item) => [`${item.document_type}:${item.document_number}`, String(item.amount)]),
  ));

  useEffect(() => {
    let active = true;
    setLoadingCustomers(true);
    setCustomerError('');
    getBankReconciliationCustomers(runId).then((response) => {
      if (!active) return;
      setCustomers(response.data);
      setCustomerSearch((current) => {
        if (!initialCustomer || current) return current;
        return response.data.find((customer) => customer.customer_id === initialCustomer)?.customer_name ?? '';
      });
    }).catch(() => {
      if (active) setCustomerError(t('reconciliation.edit.customersError'));
    }).finally(() => { if (active) setLoadingCustomers(false); });
    return () => { active = false; };
  }, [initialCustomer, reload, runId, t]);

  useEffect(() => {
    let active = true;
    setLoadingInvoices(true);
    setInvoiceError('');
    setOptions(null);
    if (!customerId) {
      setLoadingInvoices(false);
      return () => { active = false; };
    }
    getBankReconciliationInvoiceOptions(runId, suggestion.suggestion_id, customerId)
      .then((response) => {
        if (!active) return;
        setOptions(response.data);
        if (suggestOnLoad.current) {
          useSuggested(response.data);
          suggestOnLoad.current = false;
        }
      }).catch(() => {
        if (active) setInvoiceError(t('reconciliation.edit.invoicesError'));
      }).finally(() => { if (active) setLoadingInvoices(false); });
    return () => { active = false; };
  }, [runId, suggestion.suggestion_id, suggestion.customer_id, customerId, reload, t]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (loadingCustomers || loadingInvoices || customerError || invoiceError || invalidTotal || !options) return;
    const allocations: BankReconciliationInvoiceAllocation[] = invoices.flatMap((invoice) => {
      const amount = Number(amounts[`${invoice.document_type}:${invoice.document_number}`] || 0);
      if (amount <= 0) return [];
      return [{
        document_type: invoice.document_type,
        document_number: invoice.document_number,
        document_id: invoice.document_id,
        document_date: invoice.document_date,
        due_date: invoice.due_date,
        invoice_total: invoice.original_amount,
        open_balance: invoice.open_balance,
        amount,
        remaining_balance: Number((Number(invoice.open_balance) - amount).toFixed(2)),
      }];
    });
    onSave({
      version: suggestion.version,
      action: 'update',
      ...(customerId !== suggestion.customer_id || confirmCustomer ? {
        customer_id: customerId,
        customer_name: options.customer_name,
      } : {}),
      payment,
      allocations,
      reason: reason || undefined,
    });
  };

  const selectCustomer = (customer: BankReconciliationCustomerIdentity) => {
    suggestOnLoad.current = true;
    setCustomerId(customer.customer_id);
    setCustomerSearch(customer.customer_name);
    setCustomerListOpen(false);
    setActiveCustomerIndex(0);
    setAmounts({});
  };

  const changeCustomerSearch = (value: string) => {
    setCustomerSearch(value);
    setCustomerListOpen(true);
    setActiveCustomerIndex(0);
    const selected = customers.find((customer) => customer.customer_id === customerId);
    if (selected && value !== selected.customer_name) {
      suggestOnLoad.current = true;
      setCustomerId('');
      setAmounts({});
    }
  };

  const handleCustomerKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setCustomerListOpen(false);
      return;
    }
    if (!customerListOpen || filteredCustomers.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveCustomerIndex((index) => Math.min(index + 1, filteredCustomers.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveCustomerIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      selectCustomer(filteredCustomers[activeCustomerIndex] ?? filteredCustomers[0]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="presentation" onMouseDown={onClose}>
      <form
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`edit-${suggestion.suggestion_id}`}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
      >
        <h2 id={`edit-${suggestion.suggestion_id}`} className="text-lg font-semibold text-gray-900">
          {t('reconciliation.edit.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-600">{t('reconciliation.saveHelp')}</p>
        {(loadingCustomers || loadingInvoices) && (
          <div
            role="status"
            aria-live="polite"
            className="mt-3 flex items-center gap-2 rounded-lg bg-primary-50 px-3 py-2 text-sm font-medium text-primary-800"
          >
            <span
              className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary-200 border-t-primary-700"
              aria-hidden="true"
            />
            {t('reconciliation.edit.loadingEditor')}
          </div>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="relative text-sm font-medium text-gray-700">
            <label htmlFor={`customer-search-${suggestion.suggestion_id}`}>
              {t('reconciliation.edit.customer')}
            </label>
            <input
              id={`customer-search-${suggestion.suggestion_id}`}
              type="text"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={customerListOpen}
              aria-controls={`customer-options-${suggestion.suggestion_id}`}
              aria-activedescendant={customerListOpen && filteredCustomers[activeCustomerIndex]
                ? `customer-option-${suggestion.suggestion_id}-${filteredCustomers[activeCustomerIndex].customer_id}`
                : undefined}
              value={customerSearch}
              placeholder={t('reconciliation.edit.searchCustomer')}
              disabled={loadingCustomers || saving || !!customerError}
              onFocus={() => setCustomerListOpen(true)}
              onBlur={() => setCustomerListOpen(false)}
              onChange={(event) => changeCustomerSearch(event.target.value)}
              onKeyDown={handleCustomerKeyDown}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            {customerListOpen && !loadingCustomers && !customerError && (
              <div
                id={`customer-options-${suggestion.suggestion_id}`}
                role="listbox"
                className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
              >
                {filteredCustomers.map((customer, index) => (
                  <button
                    id={`customer-option-${suggestion.suggestion_id}-${customer.customer_id}`}
                    key={customer.customer_id}
                    type="button"
                    role="option"
                    aria-selected={customer.customer_id === customerId}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectCustomer(customer)}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm ${
                      index === activeCustomerIndex ? 'bg-primary-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium text-gray-900">{customer.customer_name}</span>
                    <span className="shrink-0 text-xs text-gray-400">#{customer.customer_id}</span>
                  </button>
                ))}
                {filteredCustomers.length === 0 && (
                  <p className="px-3 py-4 text-center text-sm text-gray-500">{t('common.noResults')}</p>
                )}
              </div>
            )}
            {customerId && (
              <button
                type="button"
                onClick={() => onOpenCustomer(customerId, customerSearch)}
                className="mt-1 text-xs font-medium text-primary-700 underline"
              >
                {t('reconciliation.edit.openCustomer')}
              </button>
            )}
          </div>
          <label className="text-sm font-medium text-gray-700">
            {t('reconciliation.edit.transferDate')}
            <input type="date" value={payment.transfer_date} onChange={(e) => setPayment({ ...payment, transfer_date: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
          </label>
          <label className="text-sm font-medium text-gray-700">
            {t('reconciliation.edit.reference')}
            <input value={payment.reference ?? ''} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
          </label>
          <label className="text-sm font-medium text-gray-700">
            {t('reconciliation.edit.description')}
            <input value={payment.description ?? ''} onChange={(e) => setPayment({ ...payment, description: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
          </label>
          <label className="text-sm font-medium text-gray-700 sm:col-span-2">
            {t('reconciliation.edit.comments')}
            <textarea value={payment.comments ?? ''} onChange={(e) => setPayment({ ...payment, comments: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" rows={2} />
          </label>
          {customerId === suggestion.customer_id && !suggestion.customer_confirmed_by
            && (suggestion.customer_identity_confidence ?? 0) < 0.85 && (
            <label className="flex items-start gap-2 text-sm font-medium text-gray-700 sm:col-span-2">
              <input type="checkbox" checked={confirmCustomer} disabled={saving || loadingCustomers || loadingInvoices}
                onChange={(event) => setConfirmCustomer(event.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600" />
              {t('reconciliation.edit.confirmCustomer')}
            </label>
          )}
        </div>
        {(customerError || invoiceError) && <div role="alert" className="mt-3 text-sm text-red-700">
          {customerError || invoiceError}
          <button type="button" onClick={() => setReload((value) => value + 1)} className="ms-2 underline">{t('common.retry')}</button>
        </div>}
        <fieldset className="mt-5">
          <legend className="font-medium text-gray-900">{t('reconciliation.edit.allocations')}</legend>
          <div className="mt-2 space-y-2">
            {!loadingInvoices && !invoiceError && invoices.length === 0 && <p className="text-sm text-gray-500">{t('reconciliation.edit.noInvoices')}</p>}
            {options && invoices.length > 0 && <button type="button" onClick={() => useSuggested(options)} className="text-sm font-medium text-primary-700 underline">{t('reconciliation.edit.useSuggested')}</button>}
            {invoices.map((invoice) => {
              const key = `${invoice.document_type}:${invoice.document_number}`;
              return (
                <div key={key} className="grid grid-cols-[1fr_8rem] items-center gap-3 rounded-lg bg-gray-50 p-3 text-sm">
                  <span>
                    <DocumentLink kind="invoice" documentType={invoice.document_type} documentNumber={invoice.document_number} documentDate={invoice.document_date} />
                    <span className="block text-xs text-gray-500">{t('reconciliation.invoiceDate')}: <time dateTime={invoice.document_date} dir="ltr" className="inline-block whitespace-nowrap">{invoice.document_date}</time></span>
                    <span className="block text-xs text-gray-500">{t('reconciliation.edit.openBalance')}: {invoice.open_balance}</span>
                  </span>
                  <input aria-label={`${t('reconciliation.edit.allocationAmount')} ${invoice.document_number} · ${invoice.document_date}`} type="number" min="0" max={invoice.open_balance} step="0.01" disabled={saving || loadingInvoices} value={amounts[key] ?? ''} onChange={(e) => setAmounts({ ...amounts, [key]: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2" />
                </div>
              );
            })}
          </div>
        </fieldset>
        <p className="mt-3 text-sm">{t('reconciliation.edit.allocationTotal', {
          allocated: (totalCents / 100).toFixed(2), remaining: ((paymentCents - totalCents) / 100).toFixed(2),
        })}</p>
        {invalidTotal && <p role="alert" className="mt-2 text-sm text-red-700">{t('reconciliation.edit.invalidAllocation')}</p>}
        <label className="mt-4 block text-sm font-medium text-gray-700">
          {t('reconciliation.edit.reason')}
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" rows={2} />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">{t('common.cancel')}</button>
          <button type="submit" disabled={saving || !customerId || loadingCustomers || loadingInvoices || !!customerError || !!invoiceError || invalidTotal || !options} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
